import React from 'react'
import clsx from 'clsx'
import { Logger } from 'wdc-cube'
import type { ScopeOf } from 'wdc-cube-react'
import { makeStyles } from 'tss-react/mui'
import { styled } from '@mui/material/styles'
import { FCClassContext, cardSx, classToFComponent } from 'src/utils/views'

import Paper from '@mui/material/Paper'
import Tooltip from '@mui/material/Tooltip'

import TcmProgress from './tcm_progress'
import { formatNumber, formatNumberToCompactShortForm } from './tcm_view_utils'
import { ValueScope } from '../../tcm_scopes'

const LOG = Logger.get('TCM-VAL')

const TileContainer = styled(Paper)({
    ...cardSx,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '13px 12px',
    minWidth: 0,
    flexGrow: 1,
    transition: 'border-color .15s ease, background-color .15s ease'
})

const TileValue = styled('span')({
    fontSize: 'clamp(18px, 1.5vw, 26px)',
    lineHeight: 1,
    fontWeight: 800,
    color: 'var(--ink)',
    marginBottom: 6,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
})

const TileLabel = styled('span')({
    fontSize: 11.5,
    lineHeight: 1.25,
    color: 'var(--muted)',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
})

const useStyles = makeStyles()({
    hand: {
        cursor: 'pointer',
        '&:hover': {
            borderColor: '#aebbb3'
        }
    },
    selected: {
        borderColor: '#f3d6a3',
        backgroundColor: 'var(--warning-bg)'
    },
    selectedValue: {
        color: 'var(--warning)'
    }
})

export type ValueViewProps = {
    className?: string
    style?: React.CSSProperties
    scope: ValueScope
}

export class ValueViewClass<P extends ValueViewProps> implements FCClassContext<P> {
    scope!: ScopeOf<P> & ValueScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState(props: P) {
        this.scope = props.scope as this['scope']
        this.classes = useStyles().classes
    }

    render({ className, style, scope }: P): React.JSX.Element {
        const { classes } = this
        const clickable = !!scope.onClicked

        return (
            <TileContainer
                elevation={0}
                style={style}
                className={clsx(
                    clickable ? classes.hand : undefined,
                    scope.selected ? classes.selected : undefined,
                    className
                )}
                onClick={clickable ? this.emitClick : undefined}
            >
                {!scope.loaded && <TcmProgress />}
                {scope.loaded && (
                    <>
                        <Tooltip title={formatNumber(scope.value)}>
                            <TileValue className={scope.selected ? classes.selectedValue : undefined}>
                                {formatNumberToCompactShortForm(scope.value)}
                            </TileValue>
                        </Tooltip>
                        <TileLabel
                            title={scope.captionHint || (typeof scope.caption === 'string' ? scope.caption : undefined)}
                        >
                            {scope.caption}
                        </TileLabel>
                    </>
                )}
            </TileContainer>
        )
    }

    emitClick = () => {
        if (this.scope.onClicked) {
            this.scope.onClicked().catch(LOG.caught)
        }
    }
}

export default classToFComponent(ValueViewClass, React)
